import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Box, Typography, Stack } from '@mui/material';
import BannerAppSettings, { emptyBannerAppConfig } from './BannerAppSettings';
import BannerFeedPicker from './BannerFeedPicker';
import { objectStorageApi } from '../../../api/objectStorage';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { adminApi } from '../../../api/admin';
import api from '../../../api/api';
import PageLayout from '../../../components/layout/PageLayout';
import ThumnailEditor from '../../../components/common/ThumnailEditor';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import useAdminDraftGuard, { AdminDraftProvider } from '../useAdminDraftGuard';
import BasicButton from '../../../components/button/BasicButton';
import CustomTextField from '../../../components/common/CustomTextField';
import ProgressBar from '../../../components/common/ProgressBar';
import theme from '../../../theme';

const schema = yup.object().shape({
  title: yup.string().required('제목을 입력하세요.'),
  titleEn: yup.string(),
  url: yup
    .string()
    .required('URL을 입력하세요.')
    .test('is-url', '올바른 URL 형식을 입력하세요.', (value) => {
      if (!value) return true;
      return value.startsWith('http://') || value.startsWith('https://');
    }),
  urlEn: yup.string().test('is-url', '올바른 URL 형식을 입력하세요.', (value) => {
    if (!value) return true;
    return value.startsWith('http://') || value.startsWith('https://');
  }),
  imageUrlEn: yup.string().test('is-url', '올바른 URL 형식을 입력하세요.', (value) => {
    if (!value) return true;
    return value.startsWith('http://') || value.startsWith('https://');
  }),
});

const BannerForm = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const [deviceType, setDeviceType] = useState(searchParams.get('deviceType') || 'PC');

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      title: '',
      titleEn: '',
      url: '',
      urlEn: '',
      imageUrlEn: '',
    },
  });

  const [thumbnailImage, setThumbnailImage] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState('');
  const [imageError, setImageError] = useState('');
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [appConfig, setAppConfig] = useState({ ...emptyBannerAppConfig });
  const [appFile, setAppFile] = useState(null);
  const [saveError, setSaveError] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);
  const [cropBusy, setCropBusy] = useState(false);
  const draft = useAdminDraftGuard(
    { fields: watch(), deviceType, thumbnailImage, thumbnailPreview, appConfig, appFile },
    { enabled: !isLoading && !loadFailed, busy: isSubmitting || cropBusy },
  );
  const startCrop = useCallback(() => {
    setAppFile(null);
    setAppConfig((previous) => ({ ...previous, imageUrl: '' }));
  }, []);
  const selectFeed = ({ ko, en }) => {
    const url = `${window.location.origin}/feed/${encodeURIComponent(ko.slug)}`;
    setValue('title', ko.title);
    setValue('titleEn', en.title);
    setValue('url', url);
    setValue('urlEn', url);
    setValue('imageUrlEn', '');
    setThumbnailImage(null);
    setThumbnailPreview('');
    setAppFile(null);
    setAppConfig((previous) => ({
      ...previous,
      feedPostId: ko.id,
      feedSlug: ko.slug,
      feedCopyMode: 'auto',
      feedImageSource: ko.imageUrl,
      imageUrl: '',
      headline: ko.title.slice(0, 160),
      headlineEn: en.title.slice(0, 160),
      subtitle: ko.excerpt.slice(0, 300),
      subtitleEn: en.excerpt.slice(0, 300),
      cropX: 0.5,
      cropY: 0.5,
      cropZoom: 1,
      showAd: false,
      sourceIcon: 'none',
      fullCreative: true,
    }));
  };

  // 편집 모드일 때 기존 데이터 로드
  useEffect(() => {
    if (isEditMode) {
      const fetchBanner = async () => {
        try {
          const response = await adminApi.getBanner(id);
          const banner = response.data;
          setDeviceType(banner.deviceType);
          setAppConfig(
            banner.appConfig || {
              ...emptyBannerAppConfig,
              appOnly: false,
              headline: banner.title,
              fullCreative: false,
            },
          );

          setValue('title', banner.title);
          setValue('titleEn', banner.titleEn || '');
          setValue('url', banner.url);
          setValue('urlEn', banner.urlEn || '');
          setValue('imageUrlEn', banner.imageUrlEn || '');

          if (banner.imageUrl) {
            setThumbnailPreview(banner.imageUrl);
          }
        } catch (error) {
          setLoadFailed(true);
          console.error('Error fetching banner:', error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchBanner();
    }
  }, [id, isEditMode, setValue]);

  const handleThumbnailUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setThumbnailImage(file);
      setImageError('');
      const reader = new FileReader();
      reader.onload = (e) => {
        setThumbnailPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleThumbnailDelete = () => {
    setThumbnailImage(null);
    setThumbnailPreview('');
  };

  const onSubmit = async (formData) => {
    if (cropBusy) return;
    if (appConfig.feedPostId && !appFile && !appConfig.imageUrl) {
      setSaveError('피드 배너의 크롭을 완료하거나 앱 이미지를 직접 선택해주세요.');
      return;
    }
    // 이미지 필수 검증 (생성 시에만, 편집 시에는 기존 이미지 유지 가능)
    if (!thumbnailImage && !thumbnailPreview && !appFile && !appConfig.imageUrl) {
      setImageError('대표 이미지를 선택하세요.');
      return;
    }

    try {
      setSaveError('');
      let savedAppConfig = appConfig;
      if (deviceType === 'MOBILE' && appFile) {
        const upload = new FormData();
        upload.append('image', appFile);
        const result = await objectStorageApi.uploadImage(upload);
        if (!result.data?.url) throw new Error('앱 이미지 업로드 결과가 없습니다.');
        savedAppConfig = { ...appConfig, imageUrl: result.data.url };
        setAppConfig(savedAppConfig);
        setAppFile(null);
      }
      // 썸네일 이미지 업로드 (새 이미지가 있는 경우에만)
      let imageUrl = savedAppConfig.feedPostId ? savedAppConfig.imageUrl : thumbnailPreview;
      if (!imageUrl && deviceType === 'MOBILE') imageUrl = savedAppConfig.imageUrl;
      if (thumbnailImage) {
        const imageFormData = new FormData();
        imageFormData.append('image', thumbnailImage);
        const { data: response } = await api.postAuth('/images/upload', imageFormData);
        imageUrl = response.data.url;
      }

      if (isEditMode) {
        // 배너 수정
        await adminApi.updateBanner(id, {
          ...(deviceType === 'MOBILE' ? { appConfig: savedAppConfig } : {}),
          title: formData.title,
          titleEn: formData.titleEn?.trim() || null,
          url: formData.url,
          urlEn: formData.urlEn?.trim() || null,
          imageUrl,
          imageUrlEn: formData.imageUrlEn?.trim() || null,
        });
      } else {
        // 배너 생성
        await adminApi.createBanner({
          ...(deviceType === 'MOBILE' ? { appConfig: savedAppConfig } : {}),
          title: formData.title,
          titleEn: formData.titleEn?.trim() || null,
          url: formData.url,
          urlEn: formData.urlEn?.trim() || null,
          imageUrl,
          imageUrlEn: formData.imageUrlEn?.trim() || null,
          deviceType,
        });
      }

      draft.markClean();
      navigate('/admin?mainTab=ads&subTab=home-banner');
    } catch (error) {
      setSaveError(error.response?.data?.msg || error.message || '저장하지 못했습니다.');
    }
  };

  const handleCancel = () => {
    if (!draft.confirmDiscard()) return;
    draft.markClean();
    navigate('/admin?mainTab=ads&subTab=home-banner');
  };

  return (
    <PageLayout>
      <ProgressBar isLoading={isLoading}>
        <Box sx={{ maxWidth: '780px', mx: 'auto' }}>
          <Stack
            component="fieldset"
            disabled={isSubmitting}
            spacing={{ xs: '30px', lg: '50px' }}
            sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}
          >
            {loadFailed && (
              <Alert severity="error">
                배너를 불러오지 못했습니다. 목록으로 돌아가 다시 열어주세요.
              </Alert>
            )}
            {saveError && <Alert severity="error">{saveError}</Alert>}
            {deviceType === 'MOBILE' && (
              <BannerFeedPicker
                value={appConfig}
                onChange={setAppConfig}
                onSelect={selectFeed}
                onFileChange={setAppFile}
                onBusyChange={setCropBusy}
                onCropStart={startCrop}
              />
            )}
            <Stack spacing={'20px'}>
              <Stack spacing={'8px'}>
                <Typography fontSize={14} fontWeight={'regular'} color={theme.palette.black[7]}>
                  제목 <span style={{ color: theme.palette.red }}>*</span>
                </Typography>
                <CustomTextField
                  fullWidth
                  {...register('title')}
                  error={!!errors.title}
                  helperText={errors.title?.message}
                  placeholder="제목을 입력하세요."
                />
              </Stack>
              <Stack spacing={'8px'}>
                <Typography fontSize={14} fontWeight={'regular'} color={theme.palette.black[7]}>
                  영문 제목 (선택)
                </Typography>
                <CustomTextField
                  fullWidth
                  {...register('titleEn')}
                  placeholder="비워두면 한국어 제목을 표시합니다."
                />
              </Stack>
              <Stack spacing={'8px'}>
                <Typography fontSize={14} fontWeight={'regular'} color={theme.palette.black[7]}>
                  URL <span style={{ color: theme.palette.red }}>*</span>
                </Typography>
                <CustomTextField
                  fullWidth
                  {...register('url')}
                  error={!!errors.url}
                  helperText={errors.url?.message}
                  placeholder="URL을 입력하세요."
                />
              </Stack>
              <Stack spacing={'8px'}>
                <Typography fontSize={14} fontWeight={'regular'} color={theme.palette.black[7]}>
                  영문 환경 URL (선택)
                </Typography>
                <CustomTextField
                  fullWidth
                  {...register('urlEn')}
                  error={!!errors.urlEn}
                  helperText={errors.urlEn?.message}
                  placeholder="비워두면 한국어 환경 URL을 사용합니다."
                />
              </Stack>
              <Stack spacing={'8px'}>
                <Typography fontSize={14} fontWeight={'regular'} color={theme.palette.black[7]}>
                  대표 이미지 <span style={{ color: theme.palette.red }}>*</span>
                </Typography>
                <ThumnailEditor
                  thumbnailPreview={thumbnailPreview}
                  handleThumbnailUpload={handleThumbnailUpload}
                  handleThumbnailDelete={handleThumbnailDelete}
                  type={deviceType === 'MOBILE' ? 'mobileBanner' : 'banner'}
                />
                {imageError && (
                  <Typography variant="body4" color={theme.palette.red}>
                    {imageError}
                  </Typography>
                )}
              </Stack>
              <Stack spacing={'8px'}>
                <Typography fontSize={14} fontWeight={'regular'} color={theme.palette.black[7]}>
                  영문 환경 이미지 URL (선택)
                </Typography>
                <CustomTextField
                  fullWidth
                  {...register('imageUrlEn')}
                  error={!!errors.imageUrlEn}
                  helperText={errors.imageUrlEn?.message}
                  placeholder="비워두면 기본 대표 이미지를 사용합니다."
                />
              </Stack>
            </Stack>

            {/* 버튼 */}
            {deviceType === 'MOBILE' && (
              <BannerAppSettings
                value={appConfig}
                onChange={setAppConfig}
                file={appFile}
                onFileChange={setAppFile}
                fallbackImage={thumbnailPreview}
              />
            )}
            <Box width={'100%'} display={'flex'} justifyContent={'center'}>
              <Box display="flex" gap={1} width={{ xs: '100%', md: 'auto' }}>
                <BasicButton
                  fullWidth
                  variant="outlined"
                  onClick={handleCancel}
                  sx={{ width: { md: '200px', lg: '110px' }, height: '48px' }}
                >
                  취소
                </BasicButton>
                <BasicButton
                  fullWidth
                  type="submit"
                  disabled={isSubmitting || loadFailed || cropBusy}
                  onClick={handleSubmit(onSubmit)}
                  sx={{ width: { md: '200px', lg: '110px' }, height: '48px' }}
                >
                  {isEditMode ? '수정' : '등록'}
                </BasicButton>
              </Box>
            </Box>
          </Stack>
        </Box>
      </ProgressBar>
    </PageLayout>
  );
};

export default function GuardedBannerForm() {
  return (
    <AdminDraftProvider>
      <BannerForm />
    </AdminDraftProvider>
  );
}
